/**
 * Sticky stack offsets.
 *
 * The single writer for the CSS variables that anything pinned beneath the
 * header reads (collection nav, landing-filter bar, product column, cart
 * drawer, quiz layout). Values come from ResizeObservers on the elements
 * themselves, so they update on the exact frame a box changes and never on
 * unrelated DOM churn.
 *
 *   --announcement-height  the promo bar (Hextom app embed) while it is fixed or
 *                          sticky; 0 when absent, scrolling with the page, or
 *                          when the header's "Promo bar" setting is Scroll over
 *   --header-height        promo bar + header section as laid out (expanded).
 *                          Stable while scrolling: use it for size math such as
 *                          `calc(100dvh - var(--header-height))`.
 *   --header-offset        promo bar + header as currently painted. Follows the
 *                          collapse: use it for `top` on sticky/fixed elements.
 *   --footer-height        consent banner height on wide viewports (>= 1200px)
 *
 * Events on document: `sticky-stack:refresh` (inbound) asks for a republish;
 * `sticky-stack:change` (outbound) fires after any publish that changed a
 * value, for consumers that cache geometry instead of reading the variables.
 *
 * Contract and consumers: library/header/Header-Architecture.md.
 */

const ROOT = document.documentElement;
const WIDE_VIEWPORT = 1200;
// App embeds render after the bundle; the promo bar's background is the fixed
// element and the source of truth for its height.
const OPTIONAL_TARGETS = ["#epb_background", ".cc-banner"];
const PROMO_BACKGROUND_Z_INDEX = 3;

const observed = new Set();
const resizeObserver = new ResizeObserver(() => publish());

const height = (selector) => document.querySelector(selector)?.offsetHeight || 0;
const px = (value) => `${Math.round(value)}px`;

const published = {};
function set(name, value) {
  const next = px(value);
  if (published[name] === next) return false;
  published[name] = next;
  ROOT.style.setProperty(name, next);
  return true;
}

// A promo bar that scrolls away with the page must not push the stuck header
// down, so it only counts while its computed position pins it - and not at all
// when the header section is set to scroll over it (data-promo-bar="overlap",
// mirroring the app's own non-sticky setting).
function announcementHeight() {
  if (document.querySelector("sticky-header")?.dataset.promoBar === "overlap") return 0;
  const bar = document.querySelector("#epb_background");
  if (!bar) return 0;
  const { position } = getComputedStyle(bar);
  return position === "fixed" || position === "sticky" ? bar.offsetHeight : 0;
}

function publish() {
  const announcement = announcementHeight();
  const footer = window.innerWidth >= WIDE_VIEWPORT ? height(".cc-banner") : 0;
  // header.js reports the mobile tabs band as collapsed while it is slid away;
  // the box keeps its height (no reflow) so the painted offset subtracts it.
  const collapsed = parseFloat(document.querySelector("sticky-header")?.dataset.collapsedHeight) || 0;

  const changed = [
    set("--announcement-height", announcement),
    set("--header-height", announcement + height(".section-header")),
    set("--header-offset", announcement + height("sticky-header") - collapsed),
    set("--footer-height", footer),
  ].some(Boolean);
  if (changed) document.dispatchEvent(new CustomEvent("sticky-stack:change"));
}

function observe(selector) {
  const element = document.querySelector(selector);
  if (!element || observed.has(element)) return false;
  observed.add(element);
  resizeObserver.observe(element);
  return true;
}

function attachOptionalTargets() {
  let found = false;
  OPTIONAL_TARGETS.forEach((selector) => {
    if (observe(selector)) found = true;
  });
  // Keep the fixed promo bar above page sections (they sit at z-index auto/3).
  const promoBackground = document.querySelector("#epb_background");
  if (promoBackground) promoBackground.style.zIndex = String(PROMO_BACKGROUND_Z_INDEX);
  return found;
}

observe(".section-header");
observe("sticky-header");
attachOptionalTargets();
publish();

// The optional targets are injected by apps at an unknown point. Watch the DOM
// only to find them; the callback never measures anything.
const arrivals = new MutationObserver(() => {
  if (attachOptionalTargets()) publish();
});
arrivals.observe(document.body, { childList: true, subtree: true });

window.addEventListener("resize", publish);
document.addEventListener("sticky-stack:refresh", publish);
// Theme-editor re-renders replace the header section wholesale.
document.addEventListener("shopify:section:load", () => {
  observe(".section-header");
  observe("sticky-header");
  publish();
});
// The consent banner resizes itself without a DOM insertion when it loads or
// changes state; the ResizeObserver handles most of it, these cover the rest.
["consentmoSignal_onLoad", "consentmoSignal"].forEach((name) => {
  document.addEventListener(name, () => setTimeout(publish, 50));
});
